import React from 'react';
import { Link } from 'react-router-dom';
import {
	Card, Input, Button, Table, Modal, Descriptions, Divider, Tag,
	Typography, Pagination, Empty, Alert, message,
} from 'antd';
import {
	SearchOutlined, CarOutlined, EyeOutlined, IdcardOutlined,
	StopOutlined, CheckCircleOutlined, InfoCircleOutlined, EditOutlined,
	PlusCircleOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { initHandler, ajaxViewHandler, extractResult } from '../../store/actions';
import Config from '../../Config';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { toastError, toastWarning } from '../../utils/toast';

const { Text } = Typography;
const { TextArea } = Input;

const configClass = {
	title: 'Kendaraan',
	apiUrl: {
		data: 'master/api/kendaraan-pencarian/',
		nonaktifkan: (id) => `master/api/kendaraan-pencarian/${id}/nonaktifkan/`,
	},
};

const MIN_SEARCH_LENGTH = 3;

const config = new Config();

/**
 * Menu Master > Kendaraan - alat PENCARIAN read-only ke registry kendaraan
 * (nopol/noka/nosin/NIK/nama pemilik). SENGAJA tidak menampilkan tabel
 * penuh secara default (privasi data pemilik) - hasil baru muncul setelah
 * pencarian eksplisit (submit, bukan auto-search per-ketikan seperti menu
 * master lain), selaras dengan batas minimal 3 karakter di backend.
 */
class DataKendaraan extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			searchInput: '',
			hasSearched: false,
			loadingData: false,
			dataSource: [],
			pagination: { pageSize: 10, current: 1, total: 0 },
			isMobile: window.innerWidth <= 768,
			detailVisible: false,
			detailRow: null,
			modalNonaktifkan: null, // row kendaraan yang sedang mau dinonaktifkan
			alasanNonaktifkan: '',
			loadingNonaktifkan: false,
		};
	}

	UNSAFE_componentWillMount() { this.props.initHandler(); }

	componentDidMount() {
		this._handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });
		window.addEventListener('resize', this._handleResize);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._handleResize);
	}

	_doSearch = (search, limit = this.state.pagination.pageSize, offset = 0) => {
		this.setState({ loadingData: true, hasSearched: true });
		this.props.ajaxViewHandler('get', `${configClass.apiUrl.data}?search=${encodeURIComponent(search)}&limit=${limit}&offset=${offset}`)
			.then(() => {
				if (this.props.isSuccess) {
					const data = this.props.responseMsg;
					this.setState(prev => ({
						dataSource: data.results || [],
						pagination: { ...prev.pagination, total: data.count || 0, current: Math.floor(offset / limit) + 1 },
					}));
				} else {
					toastError('Gagal memuat data, coba lagi');
					this.setState({ dataSource: [], pagination: { ...this.state.pagination, total: 0 } });
				}
				this.setState({ loadingData: false });
			})
			.catch(() => {
				toastError('Gagal request data, coba lagi');
				this.setState({ loadingData: false });
			});
	};

	handleSubmit = () => {
		const term = this.state.searchInput.trim();
		if (term.length < MIN_SEARCH_LENGTH) {
			toastWarning(`Masukkan minimal ${MIN_SEARCH_LENGTH} karakter untuk mencari`);
			return;
		}
		this._doSearch(term, this.state.pagination.pageSize, 0);
	};

	handleTableChange = (paginationInfo) => {
		this.setState(prev => ({ pagination: { ...prev.pagination, pageSize: paginationInfo.pageSize } }));
		this._doSearch(this.state.searchInput.trim(), paginationInfo.pageSize, (paginationInfo.current - 1) * paginationInfo.pageSize);
	};

	handleMobilePageChange = (page, pageSize) => {
		this._doSearch(this.state.searchInput.trim(), pageSize, (page - 1) * pageSize);
	};

	openDetail = (row) => this.setState({ detailVisible: true, detailRow: row });
	closeDetail = () => this.setState({ detailVisible: false, detailRow: null });

	openModalNonaktifkan = (row) => this.setState({ modalNonaktifkan: row, alasanNonaktifkan: '' });
	closeModalNonaktifkan = () => {
		if (this.state.loadingNonaktifkan) return;
		this.setState({ modalNonaktifkan: null, alasanNonaktifkan: '' });
	};

	submitNonaktifkan = () => {
		const { modalNonaktifkan, alasanNonaktifkan } = this.state;
		if (!modalNonaktifkan) return;
		const alasan = alasanNonaktifkan.trim();
		if (!alasan) {
			message.error({ content: 'Alasan wajib diisi', duration: 2, key: 'toast' });
			return;
		}
		this.setState({ loadingNonaktifkan: true });
		this.props.ajaxViewHandler('post', configClass.apiUrl.nonaktifkan(modalNonaktifkan.id), { alasan })
			.then((action) => {
				const { isSuccess, responseMsg } = extractResult(action);
				this.setState({ loadingNonaktifkan: false });
				if (isSuccess) {
					message.success({ content: (responseMsg && responseMsg.status) || 'Kendaraan dinonaktifkan', duration: 3, key: 'toast' });
					this.setState((prev) => ({
						modalNonaktifkan: null,
						alasanNonaktifkan: '',
						// Hilangkan dari hasil pencarian saat ini (backend scope-nya
						// status_deleted=False, baris ini otomatis tidak lagi muncul
						// kalau dicari ulang) - tidak perlu re-fetch, cukup filter lokal.
						dataSource: prev.dataSource.filter((r) => r.id !== modalNonaktifkan.id),
						pagination: { ...prev.pagination, total: Math.max(0, prev.pagination.total - 1) },
						detailVisible: prev.detailRow && prev.detailRow.id === modalNonaktifkan.id ? false : prev.detailVisible,
					}));
				} else {
					message.error({ content: (responseMsg && responseMsg.error) || 'Gagal menonaktifkan, coba lagi', duration: 4, key: 'toast' });
				}
			}).catch(() => {
				this.setState({ loadingNonaktifkan: false });
				message.error({ content: 'Gagal request data, coba lagi', duration: 2, key: 'toast' });
			});
	};

	_fmtTanggal = (val) => (val ? moment(val).format('DD-MM-YYYY') : '-');

	renderStatusTag(row) {
		return row.is_block
			? <Tag icon={<StopOutlined />} style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322' }}>Diblokir</Tag>
			: <Tag icon={<CheckCircleOutlined />} style={{ borderRadius: 20, fontWeight: 600, fontSize: 11, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}>Aktif</Tag>;
	}

	// Edit hanya utk superadmin/admin - gerbang ganda dgn backend
	// (IsAdminOrSuperadminForWrite), ini cuma soal tampilan tombolnya.
	canEdit = () => this.props.level_user === 1 || this.props.level_user === 2;

	_buildColumns() {
		const { pagination } = this.state;
		const canEdit = this.canEdit();
		return [
			{ title: 'No', key: 'no', width: 52, align: 'center', render: (_, __, index) => <Text style={{ fontSize: 12, color: '#888' }}>{(pagination.current - 1) * pagination.pageSize + index + 1}</Text> },
			{ title: 'Nomor Polisi', dataIndex: 'nomor_polisi', key: 'nomor_polisi', width: 140, render: (val) => <Text strong style={{ fontSize: 13, color: '#0f2847', letterSpacing: 0.5 }}>{val || '-'}</Text> },
			{ title: 'Nama Pemilik', dataIndex: 'pemilik_nama', key: 'pemilik_nama', render: (val) => <Text style={{ fontSize: 13 }}>{val || '-'}</Text> },
			{ title: 'Merek / Tipe', key: 'merek_tipe', width: 170, render: (_, row) => <Text style={{ fontSize: 13 }}>{[row.merek_nama, row.tipe_nama].filter(Boolean).join(' / ') || '-'}</Text> },
			{ title: 'Tahun', dataIndex: 'tahun_buat', key: 'tahun_buat', width: 80, align: 'center' },
			{ title: 'Status', key: 'status', width: 110, align: 'center', render: (_, row) => this.renderStatusTag(row) },
			{
				title: 'Aksi', key: 'aksi', width: canEdit ? 250 : 90, fixed: 'right', align: 'center',
				render: (_, row) => (
					// stopPropagation di semua tombol - baris tabel punya onRow
					// onClick (openDetail), tanpa ini klik Edit/Nonaktifkan ikut
					// membuka modal Detail di belakangnya (lihat laporan 2026-10-14).
					<div style={{ display: 'flex', gap: 6, justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
						<Button size="small" icon={<EyeOutlined />} onClick={() => this.openDetail(row)} style={{ borderRadius: 6 }}>
							Detail
						</Button>
						{canEdit && (
							<Link to={`/admin/kendaraan/form/${row.id}`}>
								<Button size="small" type="primary" icon={<EditOutlined />} style={{ borderRadius: 6, background: '#0f2847', borderColor: '#0f2847' }}>
									Edit
								</Button>
							</Link>
						)}
						{canEdit && (
							<Button size="small" danger icon={<StopOutlined />} style={{ borderRadius: 6 }}
								onClick={() => this.openModalNonaktifkan(row)}>
								Nonaktifkan
							</Button>
						)}
					</div>
				),
			},
		];
	}

	renderMobileCard(row) {
		return (
			<Card key={row.id} style={{ borderRadius: 14, marginBottom: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0', overflow: 'hidden' }} bodyStyle={{ padding: 0 }}
				onClick={() => this.openDetail(row)}>
				<div style={{ background: 'linear-gradient(135deg, #0f284711, #1e4d8a11)', borderBottom: '1px solid #d0dff5', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
					<div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: 'linear-gradient(135deg, #0f2847, #1e4d8a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<CarOutlined style={{ color: '#fff', fontSize: 20 }} />
					</div>
					<div style={{ flex: 1, minWidth: 0 }}>
						<div style={{ fontWeight: 700, fontSize: 15, color: '#0f2847', lineHeight: 1.3 }}>{row.nomor_polisi || '-'}</div>
						<div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{row.pemilik_nama || '-'}</div>
						<div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
							<Tag style={{ borderRadius: 20, fontSize: 11, margin: 0, background: '#e6f0fb', borderColor: '#91b4e8', color: '#1e4d8a', fontWeight: 600 }}>
								{[row.merek_nama, row.tipe_nama].filter(Boolean).join(' / ') || '-'}
							</Tag>
							{this.renderStatusTag(row)}
						</div>
					</div>
					<div style={{ display: 'flex', gap: 6 }}>
						<Button size="middle" shape="circle" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); this.openDetail(row); }} />
						{this.canEdit() && (
							<Link to={`/admin/kendaraan/form/${row.id}`} onClick={(e) => e.stopPropagation()}>
								<Button size="middle" shape="circle" type="primary" icon={<EditOutlined />} style={{ background: '#0f2847', borderColor: '#0f2847' }} />
							</Link>
						)}
						{this.canEdit() && (
							<Button size="middle" shape="circle" danger icon={<StopOutlined />}
								onClick={(e) => { e.stopPropagation(); this.openModalNonaktifkan(row); }} />
						)}
					</div>
				</div>
			</Card>
		);
	}

	renderDetailModal() {
		const { detailVisible, detailRow, isMobile } = this.state;
		if (!detailRow) return null;
		const d = detailRow;
		return (
			<Modal
				title={<span><CarOutlined style={{ color: '#0f2847', marginRight: 8 }} />Detail Kendaraan — {d.nomor_polisi}</span>}
				open={detailVisible} onCancel={this.closeDetail} footer={null} destroyOnClose
				width={isMobile ? '95%' : 640}
			>
				{d.is_block && (
					<div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '8px 14px', marginBottom: 14, fontSize: 12, color: '#a8071a' }}>
						<StopOutlined style={{ marginRight: 8 }} />
						Kendaraan ini berstatus <b>diblokir</b>{d.alasan_blokir ? ` — ${d.alasan_blokir}` : ''}.
					</div>
				)}

				<Divider orientation="left" plain style={{ fontSize: 12 }}><IdcardOutlined style={{ marginRight: 6 }} />Data Pemilik</Divider>
				<Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 2 }} style={{ marginBottom: 12 }}>
					<Descriptions.Item label="Nama Pemilik">{d.pemilik_nama || '-'}</Descriptions.Item>
					<Descriptions.Item label="NIK">{d.pemilik_nik || '-'}</Descriptions.Item>
					<Descriptions.Item label="Alamat" span={2}>{d.pemilik_alamat || '-'}</Descriptions.Item>
					<Descriptions.Item label="No HP">{d.pemilik_no_hp || '-'}</Descriptions.Item>
					<Descriptions.Item label="Kepemilikan Ke">{d.kepemilikan_ke || '-'}</Descriptions.Item>
				</Descriptions>

				<Divider orientation="left" plain style={{ fontSize: 12 }}><CarOutlined style={{ marginRight: 6 }} />Data Kendaraan</Divider>
				<Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 2 }} style={{ marginBottom: 12 }}>
					<Descriptions.Item label="Nomor Polisi"><Text strong>{d.nomor_polisi || '-'}</Text></Descriptions.Item>
					<Descriptions.Item label="Status">{this.renderStatusTag(d)}</Descriptions.Item>
					<Descriptions.Item label="Nomor Rangka">{d.nomor_rangka || '-'}</Descriptions.Item>
					<Descriptions.Item label="Nomor Mesin">{d.nomor_mesin || '-'}</Descriptions.Item>
					<Descriptions.Item label="Merek">{d.merek_nama || '-'}</Descriptions.Item>
					<Descriptions.Item label="Tipe">{d.tipe_nama || '-'}</Descriptions.Item>
					<Descriptions.Item label="Jenis Kendaraan">{d.jenis_kendaraan_nama || '-'}</Descriptions.Item>
					<Descriptions.Item label="Tahun Buat">{d.tahun_buat || '-'}</Descriptions.Item>
					<Descriptions.Item label="Warna">{d.warna || '-'}</Descriptions.Item>
					<Descriptions.Item label="Warna Plat">{d.warna_plat_nama || '-'}</Descriptions.Item>
					<Descriptions.Item label="Fungsi">{d.fungsi_nama || '-'}</Descriptions.Item>
					<Descriptions.Item label="Silinder (CC)">{d.silinder || '-'}</Descriptions.Item>
					<Descriptions.Item label="Kepemilikan">{d.kepemilikan_nama || '-'}</Descriptions.Item>
					<Descriptions.Item label="No BPKB">{d.no_bpkb || '-'}</Descriptions.Item>
				</Descriptions>

				<Divider orientation="left" plain style={{ fontSize: 12 }}><InfoCircleOutlined style={{ marginRight: 6 }} />Tanggal Penting</Divider>
				<Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 2 }}>
					<Descriptions.Item label="STNK Berlaku s/d">{this._fmtTanggal(d.stnk_akhir)}</Descriptions.Item>
					<Descriptions.Item label="SWDKLLJ s/d">{this._fmtTanggal(d.sd_swdkllj)}</Descriptions.Item>
					<Descriptions.Item label="Pajak (STNK) s/d">{this._fmtTanggal(d.sd_stnk)}</Descriptions.Item>
					<Descriptions.Item label="Notice s/d">{this._fmtTanggal(d.sd_notice)}</Descriptions.Item>
				</Descriptions>
			</Modal>
		);
	}

	renderModalNonaktifkan() {
		const { modalNonaktifkan, alasanNonaktifkan, loadingNonaktifkan, isMobile } = this.state;
		if (!modalNonaktifkan) return null;
		const d = modalNonaktifkan;
		return (
			<Modal
				title={<span><StopOutlined style={{ color: '#cf1322', marginRight: 8 }} />Nonaktifkan Kendaraan</span>}
				open={true}
				onCancel={this.closeModalNonaktifkan}
				closable={!loadingNonaktifkan}
				maskClosable={false}
				width={isMobile ? '94%' : 480}
				footer={[
					<Button key="batal" onClick={this.closeModalNonaktifkan} disabled={loadingNonaktifkan}>Batal</Button>,
					<Button key="submit" danger type="primary" loading={loadingNonaktifkan} onClick={this.submitNonaktifkan}>
						Nonaktifkan
					</Button>,
				]}
			>
				<Alert
					type="warning" showIcon style={{ marginBottom: 16 }}
					message="Kendaraan ini akan ditandai tidak aktif (soft-delete)."
					description="Nomor polisi ini akan otomatis bebas dipakai kendaraan lain lewat Pendaftaran dari ERI. Bisa dibatalkan lewat menu Laporan > Duplikat Nopol Aktif, tab Riwayat Dinonaktifkan, kalau keliru."
				/>
				<div style={{ marginBottom: 12, fontSize: 13, color: '#595959' }}>
					<div><b>Nopol:</b> {d.nomor_polisi}</div>
					<div><b>Pemilik:</b> {d.pemilik_nama || '-'}</div>
					<div><b>Rangka/Mesin:</b> {d.nomor_rangka || '-'} / {d.nomor_mesin || '-'}</div>
				</div>
				<div style={{ marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Alasan <span style={{ color: '#cf1322' }}>*</span></div>
				<TextArea
					rows={3}
					value={alasanNonaktifkan}
					onChange={(e) => this.setState({ alasanNonaktifkan: e.target.value })}
					placeholder="Mis. kendaraan rusak total, dijual ke luar sistem, atau data keliru input."
					disabled={loadingNonaktifkan}
				/>
			</Modal>
		);
	}

	render() {
		const { searchInput, hasSearched, loadingData, dataSource, pagination, isMobile } = this.state;
		return (
			<div>
				<Helmet>
					<title>{configClass.title + ' | ' + config.get_site_info().name}</title>
					<style>{`
						.data-kendaraan-wrap .ant-table-thead > tr > th { background: linear-gradient(135deg, #0f284711, #1e4d8a11) !important; font-weight: 700; font-size: 13px; color: #0f2847; border-bottom: 2px solid #d0dff5; }
						.data-kendaraan-wrap .ant-table-tbody > tr > td { padding: 12px 16px; vertical-align: middle; }
						.data-kendaraan-wrap .ant-table-tbody > tr:hover > td { background: #e8f0fb !important; }
					`}</style>
				</Helmet>

				{this.renderDetailModal()}
				{this.renderModalNonaktifkan()}

				<Card className="data-kendaraan-wrap" style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', overflow: 'hidden' }} bodyStyle={{ padding: 0 }}>
					<div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #0f2847, #1e4d8a)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
						<div>
							<div style={{ color: '#fff', fontWeight: 700, fontSize: 17 }}><CarOutlined style={{ marginRight: 8 }} />{configClass.title}</div>
							<div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>Cari data registrasi kendaraan berdasarkan nomor polisi, nomor rangka, nomor mesin, NIK, atau nama pemilik</div>
						</div>
						{this.props.akses && this.props.akses.master_kendaraan_tambah_manual === true && (
							<Link to="/admin/kendaraan/tambah-manual">
								<Button icon={<PlusCircleOutlined />} style={{ borderRadius: 8, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff' }}>Tambah Kendaraan</Button>
							</Link>
						)}
					</div>

					<div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
						<Input.Search
							placeholder="Nomor polisi / nomor rangka / nomor mesin / NIK / nama pemilik..."
							allowClear
							enterButton={<Button type="primary" icon={<SearchOutlined />} style={{ background: '#0f2847', borderColor: '#0f2847' }}>Cari</Button>}
							size="large"
							value={searchInput}
							onChange={(e) => this.setState({ searchInput: e.target.value })}
							onSearch={this.handleSubmit}
							loading={loadingData}
							style={{ maxWidth: 560 }}
						/>
					</div>

					<div style={{ padding: isMobile ? 12 : 0 }}>
						{!hasSearched ? (
							<div style={{ padding: '48px 20px' }}>
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description={
										<span style={{ color: '#888', fontSize: 13 }}>
											Masukkan nomor polisi, nomor rangka, nomor mesin, NIK, atau nama pemilik untuk mencari data kendaraan
										</span>
									}
								/>
							</div>
						) : isMobile ? (
							<div>
								{loadingData ? <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Memuat data...</div>
									: dataSource.length === 0 ? <Empty description="Tidak ditemukan data kendaraan untuk pencarian tersebut" style={{ padding: 40 }} />
									: dataSource.map(row => this.renderMobileCard(row))}
								{pagination.total > 0 && (
									<div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
										<Pagination current={pagination.current} pageSize={pagination.pageSize} total={pagination.total} onChange={this.handleMobilePageChange} showSizeChanger={false} simple />
									</div>
								)}
							</div>
						) : (
							<Table
								columns={this._buildColumns()}
								dataSource={dataSource}
								loading={loadingData}
								rowKey="id"
								scroll={{ x: 'max-content' }}
								size="middle"
								locale={{ emptyText: <Empty description="Tidak ditemukan data kendaraan untuk pencarian tersebut" style={{ padding: 24 }} /> }}
								pagination={{
									current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
									showSizeChanger: true, pageSizeOptions: ['10', '25', '50'],
									showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} data`,
								}}
								onChange={this.handleTableChange}
								onRow={(row) => ({ onClick: () => this.openDetail(row) })}
							/>
						)}
					</div>
				</Card>
			</div>
		);
	}
}

const mapStateToProps = (state) => state.configReducers;
export default connect(mapStateToProps, { initHandler, ajaxViewHandler })(DataKendaraan);
